// Drag-time previews (plan P3): while a handle is being dragged, the chart joins the handles with straight
// segments so the drag feels live. On release the forecast is saved and the chart draws the API's exact curve.
// Nothing else in the app uses these.

import { idx, N } from "@/lib/months";
import type { Month } from "@/model/types";

type Pt = [number, number];

function joinPoints(pts: Pt[], n: number): number[] {
	const out = new Array<number>(n).fill(pts[0]?.[1] ?? 0);
	let seg = 0;
	for (let t = 0; t < n; t++) {
		while (seg < pts.length - 2 && t > pts[seg + 1][0]) seg++;
		const [x0, y0] = pts[seg];
		const [x1, y1] = pts[Math.min(seg + 1, pts.length - 1)];
		out[t] = t >= pts[pts.length - 1][0] ? pts[pts.length - 1][1] : x1 === x0 ? y1 : y0 + ((y1 - y0) * (t - x0)) / (x1 - x0);
	}
	return out;
}

/** "Chance by date" handles joined by straight lines from 0 at now; flat after the last handle. */
export function previewCurve(by: Record<Month, number>, n = N): number[] {
	const pts: Pt[] = [[-1, 0]];
	let last = 0;
	for (const m of Object.keys(by).sort()) {
		const x = idx(m);
		if (x < 0) continue;
		last = Math.max(last, Math.min(0.99, Math.max(0, by[m])));
		pts.push([x, last]);
	}
	if (pts.length === 1) return new Array<number>(n).fill(0);
	return joinPoints(pts, n);
}

/** Low / likely / high handles joined by straight lines from `start` (the last actual) at now. */
export function previewRange(range: Record<Month, [number, number, number]>, start: number | null, n = N): { lo: number[]; mid: number[]; hi: number[] } {
	const months = Object.keys(range)
		.sort()
		.filter((m) => idx(m) >= 0);
	const s = start ?? (months.length ? range[months[0]][1] : 0);
	const band = (k: 0 | 1 | 2) => joinPoints([[-1, s], ...months.map((m) => [idx(m), range[m][k]] as Pt)], n);
	return { lo: band(0), mid: band(1), hi: band(2) };
}
